import type { Clock, IdGenerator } from "../ports/clock";
import type { TransactionManager } from "../ports/transaction-manager";
import type { CheckInRepository, GoalRepository, WeekPlanRepository, WeightRepository } from "../ports/repositories";
import type { PlanningConfigurationProvider } from "../ports/planning-configuration";
import type { CheckInQuestionnaire } from "../../domain/check-in/recommendation-policy";
import {
  createCompletedCheckIn,
  snapshotCurrentWeightTrend,
  type CheckIn,
} from "../../domain/check-in/check-in";
import { addCalendarDays, daysBetween } from "../../domain/shared/calendar-date";
import { completeWeekPlan, createWeekPlan, type WeekPlan } from "../../domain/planning/week-plan";
import { prepareCheckIn, type PrepareCheckInDependencies } from "./prepare-check-in";

export interface CompleteCheckInDependencies extends PrepareCheckInDependencies {
  readonly transaction: TransactionManager;
  readonly ids: IdGenerator;
  readonly weekPlans: WeekPlanRepository;
  readonly checkIns: CheckInRepository;
  readonly goals: GoalRepository;
  readonly weights: WeightRepository;
  readonly configuration: PlanningConfigurationProvider;
  readonly clock: Clock;
}

export interface CompleteCheckInResult {
  readonly checkIn: CheckIn;
  readonly completedPlan: WeekPlan;
  readonly nextPlan: WeekPlan;
  readonly skippedFullWeeks: number;
}

/**
 * Atomically closes the plan, records the reviewed recommendation/override and
 * creates the calendar-aligned current plan. Elapsed catch-up days receive target
 * allocations only; no diary entries or weights are fabricated.
 */
export async function completeCheckIn(
  dependencies: CompleteCheckInDependencies,
  input: {
    readonly expectedPlanId: string;
    readonly questionnaire: CheckInQuestionnaire;
    readonly acceptedBaselineCalories: number;
    readonly acceptedDailyProteinGrams: number;
  },
): Promise<CompleteCheckInResult> {
  return dependencies.transaction.run(async () => {
    const prepared = await prepareCheckIn(dependencies, input.questionnaire);
    if (prepared.plan.id !== input.expectedPlanId) throw new Error("Week plan changed while check-in was being reviewed");
    if (!Number.isInteger(input.acceptedBaselineCalories) || input.acceptedBaselineCalories <= 0) {
      throw new Error("Accepted baseline calories must be a positive whole number");
    }
    if (!Number.isInteger(input.acceptedDailyProteinGrams) || input.acceptedDailyProteinGrams <= 0) {
      throw new Error("Accepted daily protein must be a positive whole number");
    }

    const today = dependencies.clock.today();
    const now = dependencies.clock.now();
    const nominalNextStart = addCalendarDays(prepared.plan.startDate, 7);
    const daysIntoNominalPlan = daysBetween(nominalNextStart, today);
    const skippedFullWeeks = Math.max(0, Math.floor(daysIntoNominalPlan / 7));
    const nextStartDate = addCalendarDays(nominalNextStart, skippedFullWeeks * 7);
    const isCatchUp = nextStartDate < today;
    const nextPlanId = dependencies.ids.next();
    const nextPlan = createWeekPlan({
      id: nextPlanId,
      startDate: nextStartDate,
      baselineDailyCalories: input.acceptedBaselineCalories,
      fixedDailyProteinGrams: input.acceptedDailyProteinGrams,
      macroDistribution: prepared.configuration.macroDistribution,
      dailyFloorPolicy: prepared.configuration.dailyFloorPolicy,
      recommendationPolicyVersion: prepared.configuration.recommendationPolicy.version,
      goalPhaseId: prepared.goal.id,
      now,
      catchUpThroughDate: isCatchUp ? today : undefined,
    });
    const completedPlan = completeWeekPlan(prepared.plan, now);
    const checkIn = createCompletedCheckIn({
      id: dependencies.ids.next(),
      weekPlanId: prepared.plan.id,
      dueDate: prepared.plan.checkInDueDate,
      completedDate: today,
      completedAt: now,
      questionnaire: input.questionnaire,
      recommendation: prepared.recommendation,
      acceptedBaselineCalories: input.acceptedBaselineCalories,
      baselineWasOverridden: input.acceptedBaselineCalories !== prepared.recommendation.recommendedBaselineCalories,
      previousTrend: prepared.previousTrend,
      currentTrend: prepared.currentTrend ? snapshotCurrentWeightTrend(prepared.currentTrend) : undefined,
      proteinDecision: {
        policyVersion: prepared.proteinTarget.policyVersion,
        proposedDailyGrams: prepared.proteinTarget.gramsPerDay,
        acceptedDailyGrams: input.acceptedDailyProteinGrams,
        wasOverridden: input.acceptedDailyProteinGrams !== prepared.proteinTarget.gramsPerDay,
        gramsPerKilogram: prepared.proteinTarget.gramsPerKilogram,
        referenceWeightKilograms: prepared.proteinTarget.referenceWeightKilograms,
        referenceDate: prepared.proteinTarget.referenceDate,
        source: prepared.proteinTarget.source,
      },
      nextWeekPlanId: nextPlanId,
    });

    await dependencies.weekPlans.save(completedPlan);
    await dependencies.checkIns.save(checkIn);
    await dependencies.weekPlans.save(nextPlan);
    return { checkIn, completedPlan, nextPlan, skippedFullWeeks };
  });
}
